import moment from 'moment';
import { BooleanModel } from '../primitive/boolean.model';
import { DateModel } from '../primitive/date.model';
import { TextModel } from '../primitive/text.model';
import { CompositeModel } from './composite.model';
import { ODataLTextModel } from '../low-level/notification.model';
import { TextModelOptions } from '../options/text-model-options';
import { DateModelOptions } from '../options/date-model-options';
import { BooleanModelOptions } from '../options/boolean-model-options';
import { assignModelOptions } from '../../utilities/assign-model-options';
import { ConfigConstants } from '../../config/config-constants';
import { DisplayDecorator } from '../../decorators/display-decorator';

export class SourceReplacementModel extends CompositeModel {
    public sourceName: TextModel;
    public sourceClassification: TextModel;
    public materialNumber: TextModel;
    public calibrationDate: DateModel;
    public changeIndicator: DisplayDecorator<BooleanModel>;
    public isNewEntry: boolean;
    public changeIndicatorCode: string;

    public constructor(
        sourceName = '',
        sourceClassification = '',
        materialNumber = '',
        calibrationDate = moment.invalid(),
        changeIndicator = false,
        isNewEntry = true,
        changeIndicatorCode = '',
    ) {
        super('SourceReplacementModel');
        this.sourceName = new TextModel('SourceName', sourceName);
        this.sourceClassification = new TextModel(
            'SourceClassification',
            sourceClassification,
            new TextModelOptions({
                maxLength: 6,
                required: true,
                readonly: !isNewEntry,
            }),
        );
        this.materialNumber = new TextModel(
            'MaterialNumber',
            materialNumber,
            new TextModelOptions({
                maxLength: 12,
                required: true,
                readonly: !isNewEntry,
            }),
        );

        // Setting max calibration date to today + 10 years as per Jurgen: https://evorait.atlassian.net/browse/SHP-9599
        const maxYears = 10;
        const maxFutureDate = moment().add(maxYears, 'years');
        this.calibrationDate = new DateModel(
            'CalibrationDate',
            calibrationDate,
            new DateModelOptions({ maxValue: maxFutureDate }),
        );

        this.changeIndicator = new DisplayDecorator<BooleanModel>(
            '',
            new BooleanModel('ChangeIndicator', changeIndicator, new BooleanModelOptions({ required: true })),
        );

        // While loading from JSON, set the change indicator code from the saved value
        this.changeIndicatorCode = changeIndicatorCode;
        this.isNewEntry = isNewEntry;
        if (isNewEntry) {
            this.changeIndicatorCode = ConfigConstants.SOURCE_REPLACEMENT_INDICATOR.ADDED;
        }

        // While loading from JSON, if calibration date is valid, then remove validations from change indicator
        if (moment(calibrationDate).isValid()) {
            this.removeChangeIndicatorValidations();
        }

        // While loading from JSON, if source replacement is deleted, then remove validations from change indicator
        if (changeIndicatorCode === ConfigConstants.SOURCE_REPLACEMENT_INDICATOR.DELETED) {
            this.disableChangeIndicator();
            this.removeChangeIndicatorValidations();
        }
    }

    public changeIndicatorChanged(): void {
        if (this.changeIndicator.value) {
            this.changeIndicatorCode = ConfigConstants.SOURCE_REPLACEMENT_INDICATOR.NO_CHANGE;
            this.calibrationDate.value = moment.invalid();
        } else {
            this.changeIndicatorCode = '';
            this.resetChangeIndicatorValidations();
        }
    }

    public calibrationDateChanged(): void {
        this.removeChangeIndicatorValidations();
        if (!this.isNewEntry) {
            this.changeIndicatorCode = ConfigConstants.SOURCE_REPLACEMENT_INDICATOR.UPDATED;
        }
    }

    public sourceReplacementDeleted(): void {
        if (this.changeIndicatorCode !== ConfigConstants.SOURCE_REPLACEMENT_INDICATOR.ADDED) {
            this.changeIndicatorCode = ConfigConstants.SOURCE_REPLACEMENT_INDICATOR.DELETED;
            this.removeChangeIndicatorValidations();
            this.disableChangeIndicator();
        }
    }

    public sourceReplacementRestored(): void {
        this.changeIndicatorCode = '';
        this.calibrationDate.value = moment.invalid();
        this.resetChangeIndicatorValidations();
    }

    private disableChangeIndicator(): void {
        this.changeIndicator = assignModelOptions(
            this.changeIndicator,
            {
                ...this.changeIndicator.options,
                disabled: true,
            },
            this,
        );
    }

    private removeChangeIndicatorValidations(): void {
        this.changeIndicator = assignModelOptions(
            this.changeIndicator,
            {
                ...this.changeIndicator.options,
                required: false,
            },
            this,
        );
    }

    private resetChangeIndicatorValidations(): void {
        this.changeIndicator = assignModelOptions(
            this.changeIndicator,
            {
                ...this.changeIndicator.options,
                required: true,
                disabled: false,
            },
            this,
        );
    }

    public static fromOData(data: ODataLTextModel): SourceReplacementModel {
        const [sourceName, sourceClassification, materialNumber] = data.TextLine.split(
            ConfigConstants.SOURCE_REPLACEMENT_TEXT_SPLITTER,
        );
        return new SourceReplacementModel(
            sourceName,
            sourceClassification,
            materialNumber,
            moment.invalid(),
            false,
            false,
        );
    }

    public toJSON(): JsonSourceReplacementModel {
        return {
            sourceName: this.sourceName.value,
            sourceClassification: this.sourceClassification.value,
            materialNumber: this.materialNumber.value,
            calibrationDate: this.calibrationDate.value,
            changeIndicator: this.changeIndicator.value,
            isNewEntry: this.isNewEntry,
            changeIndicatorCode: this.changeIndicatorCode,
        };
    }
}

export class SourceReplacementModelFromJson extends SourceReplacementModel {
    public constructor(data: JsonSourceReplacementModel) {
        super(
            data.sourceName,
            data.sourceClassification,
            data.materialNumber,
            data.calibrationDate ? moment(data.calibrationDate) : moment.invalid(),
            data.changeIndicator,
            data.isNewEntry,
            data.changeIndicatorCode,
        );
    }
}

export interface JsonSourceReplacementModel {
    sourceName: string;
    sourceClassification: string;
    materialNumber: string;
    calibrationDate: moment.Moment;
    changeIndicator: boolean;
    isNewEntry: boolean;
    changeIndicatorCode: string;
}
